import { IComponentOptions, IRootScopeService, IScope } from "angular";
import { CommentService } from "@gtmhub/comments/services/comment.service";
import { GtmhubController } from "@gtmhub/core";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { localize } from "@gtmhub/localization";
import { Ng1ConfirmDeleteService } from "@gtmhub/shared/services/ng1-confirm-delete.service";
import { SizeOfGif } from "@gtmhub/shared/services/tenor-gif.service";
import { CurrentUserService, getCurrentUserId } from "@gtmhub/users";
import { Comment, Gif } from "@webapp/comments";
import { createErrorObject } from "@webapp/error-handling/error-util";

class FeedCommentComponentCtrl extends GtmhubController {
  public readonly sizeOfGif = SizeOfGif;

  public commentData: Comment;
  public metadata: { [key: string]: string };
  public isEdited = false;
  public onEditorOpen: (params: { commentId: string }) => void;
  public editorButtonsDisabled: boolean;

  get commentEditedLabel(): string {
    return `(${localize("edited")})`;
  }

  get isCurrentUserAdmin(): boolean {
    return this.currentUserService.userHasRole("admin");
  }

  get isCurrentUserCommentAuthor(): boolean {
    return this.commentData.createdBy === getCurrentUserId();
  }

  public static $inject = ["$rootScope", "CommentService", "UIErrorHandlingService", "CurrentUserService", "Ng1ConfirmDeleteService", "$scope"];

  constructor(
    private $rootScope: IRootScopeService,
    private commentService: CommentService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private currentUserService: CurrentUserService,
    private confirmDeleteService: Ng1ConfirmDeleteService,
    private $scope: IScope
  ) {
    super();
  }

  public openInlineEditor(): void {
    this.isEdited = true;
    this.onEditorOpen({ commentId: this.commentData.id });
  }

  public closeInlineEditor(): void {
    this.isEdited = false;
  }

  public openCommentDeleteConfirmation(): void {
    this.closeInlineEditor();

    this.confirmDeleteService.confirmDeleteItem({ itemKey: "comment", itemToCase: true }).subscribe(() => {
      this.commentService
        .delete(this.commentData.targetId, this.metadata)
        .then(() => this.$rootScope.$broadcast("commentDeleted", this.commentData))
        .catch((error) => this.uiErrorHandlingService.handleModal(error));
    });
  }

  editComment(args: { text: string; gif: Gif; mentions: string[] }): void {
    // in feed comments the targetId actually points to the harajuku db comment ID
    if (!this.commentData?.targetId) {
      return;
    }

    this.editorButtonsDisabled = true;
    this.$scope.$evalAsync();

    this.commentService
      .edit(
        this.commentData.targetId,
        {
          text: args.text,
          mentioned: args.mentions,
          gif: args.gif ?? { id: "", searchQuery: "" },
        },
        {
          isDeleted: Boolean(this.commentData.gif?.id && !args.gif?.id),
          isAdded: Boolean(!this.commentData.gif?.id && args.gif?.id),
        },
        this.metadata
      )
      .then((res) => {
        this.$rootScope.$broadcast("commentEdited", res);
      })
      .catch((error) => {
        this.uiErrorHandlingService.handleModal(createErrorObject(error));
      })
      .finally(() => {
        this.editorButtonsDisabled = false;
        this.$scope.$evalAsync();
      });
  }
}

export const FeedCommentComponent: IComponentOptions = {
  bindings: {
    commentData: "<",
    onMessageClick: "&",
    isEdited: "<?",
    onEditorOpen: "&",
    editable: "<",
    metadata: "<?",
    isCurrentUserViewOnly: "<?",
  },
  controller: FeedCommentComponentCtrl,
  template: require("./feed-comment.html"),
};
